import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import axios from "../../axiosConfig";
import OwnerLayout from "./OwnerLayout";
import { OwnerContext } from "../../context/OwnerContext";
import Tabs from "../commons/Tabs";
import DogInfo from "./DogInfo";
import OwnerTrainingSummaryList from "./OwnerTrainingSummaryList";
import "../../styles/OwnerMyDogStyle.css";
import OwnerAssignedExercisesList from "./OwnerAssignedExercisesList";
import OwnerAddDogModal from "./OwnerAddDogModal";
import OwnerSurveysList from "./OwnerSurveysList";
import OwnerDogGroups from "./OwnerDogGroups";
import OwnerReportsTab from "./OwnerReportsTab";

const OwnerMyDog = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { t } = useTranslation('dogs');
    const { owner, loading: ownerLoading, error: ownerError } = useContext(OwnerContext);
    const initialTab = searchParams.get('tab');
    const queryDogId = searchParams.get('dogId');

    // Multi-dog logic
    const [dogs, setDogs] = useState([]);
    const [selectedDogId, setSelectedDogId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState("");
    const [isAddDogModalOpen, setIsAddDogModalOpen] = useState(false);

    const fetchDogs = async () => {
        if (!owner) return;

        try {
            const token = localStorage.getItem("jwt");
            const response = await axios.get(`/api/v1/dog/${owner.id}/list`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const dogList = response.data || [];
            setDogs(dogList);

            if (dogList.length > 0 && !selectedDogId) {
                const dogFromQuery = queryDogId && dogList.find(d => d.id === queryDogId);
                setSelectedDogId(dogFromQuery ? queryDogId : dogList[0].id);
            }
        } catch (err) {
            setFetchError(t('fetchDogsError'));
        } finally {
            setLoading(false);
        }
    };

    const handleDogAdded = async () => {
        setLoading(true);
        await fetchDogs();
        const token = localStorage.getItem("jwt");
        const response = await axios.get(`/api/v1/dog/${owner.id}/list`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        const dogList = response.data || [];
        if (dogList.length > 0) {
            setSelectedDogId(dogList[dogList.length - 1].id);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchDogs();
    }, [owner]);

    // Sync selected dog and tab when URL query params change (e.g., clicking different notifications)
    useEffect(() => {
        if (queryDogId && dogs.length > 0 && queryDogId !== selectedDogId) {
            const dogExists = dogs.find(d => d.id === queryDogId);
            if (dogExists) {
                setSelectedDogId(queryDogId);
            }
        }
    }, [queryDogId, dogs]);

    if (ownerLoading || loading) {
        return (
            <OwnerLayout>
                <p>{t('loading')}</p>
            </OwnerLayout>
        );
    }

    if (ownerError || fetchError) {
        return (
            <OwnerLayout>
                <p className="error">{ownerError || fetchError}</p>
            </OwnerLayout>
        );
    }

    // If no dogs are found, show the "no dog" UI with a button to open the modal.
    if (dogs.length === 0) {
        return (
            <OwnerLayout>
                <div className="no-dog-container">
                    <h1>{t('noDogYet')}</h1>
                    <button className="add-dog-btn" onClick={() => setIsAddDogModalOpen(true)}>{t('addDog')}</button>
                    {isAddDogModalOpen && (
                        <OwnerAddDogModal
                            isOpen={isAddDogModalOpen}
                            onClose={() => setIsAddDogModalOpen(false)}
                            onDogAdded={handleDogAdded}
                        />
                    )}
                </div>
            </OwnerLayout>
        );
    }

    // Find the currently selected dog by ID
    const selectedDog = dogs.find((d) => d.id === selectedDogId);

    const handleDogTabClick = (dogId) => {
        setSelectedDogId(dogId);
    };

    return (
        <OwnerLayout>
            <div className="dog-details-container">
                <div className="dog-tabs-row">
                    <div className="dog-tabs-list">
                        {dogs.map((d) => (
                            <button
                                key={d.id}
                                onClick={() => handleDogTabClick(d.id)}
                                className={d.id === selectedDogId ? "dog-tab-active" : "dog-tab"}
                            >
                                {d.name}
                                {d.pendingApproval && <span className="dog-tab-pending-badge">{t('pending')}</span>}
                            </button>
                        ))}
                    </div>
                    <button
                        className="add-dog-btn"
                        onClick={() => setIsAddDogModalOpen(true)}
                    >
                        {t('addDog')}
                    </button>
                </div>

                {selectedDog ? (
                    <>
                        {selectedDog.pendingApproval && (
                            <div className="dog-pending-notice">
                                <span className="dog-pending-notice-icon">⏳</span>
                                <span>{t('pendingApprovalNotice')}</span>
                            </div>
                        )}
                        <Tabs
                            tabs={[
                                {
                                    key: "dog_info",
                                    label: t('tabs.info'),
                                    endpoint: `/api/v1/dog/details/${selectedDog.id}`,
                                    paginated: false,
                                    renderContent: (data) => <DogInfo dog={data} />,
                                },
                                {
                                    key: "recommendations",
                                    label: t('tabs.recommendations'),
                                    endpoint: `/api/v1/training-summary/dog/${selectedDog.id}`,
                                    paginated: true,
                                    renderContent: (data) => <OwnerTrainingSummaryList summaries={data} />,
                                },
                                {
                                    key: "exercises",
                                    label: t('tabs.exercises'),
                                    endpoint: `/api/v1/exercise/dog/${selectedDog.id}/assigned`,
                                    paginated: true,
                                    renderContent: (data) => <OwnerAssignedExercisesList exercises={data} />,
                                },
                                {
                                    key: "groups",
                                    label: t('tabs.groups'),
                                    endpoint: `/api/v1/groups/dog/${selectedDog.id}`,
                                    paginated: false,
                                    renderContent: (data) => <OwnerDogGroups groups={data} dogId={selectedDog.id} />,
                                },
                                {
                                    key: "surveys",
                                    label: t('tabs.surveys'),
                                    endpoint: `/api/v1/survey/dog/${selectedDog.id}/assigned`,
                                    paginated: false,
                                    renderContent: (data) => <OwnerSurveysList surveys={data} dogId={selectedDog.id} />,
                                },
                                {
                                    key: "reports",
                                    label: t('tabs.reports'),
                                    endpoint: `/api/v1/reports/dog/${selectedDog.id}/reports`,
                                    paginated: false,
                                    renderContent: (data) => <OwnerReportsTab dogOwnerReports={data} dogId={selectedDog.id} />,
                                }
                            ]}
                            activeId={selectedDog.id}
                            initialTab={initialTab}
                        />
                    </>
                ) : (
                    <div className="no-dog-container">
                        <h1>{t('noDogYet')}</h1>
                        <button className="no-dog-add-button" onClick={() => setIsAddDogModalOpen(true)}>{t('addDog')} +</button>
                    </div>
                )}
            </div>

            {isAddDogModalOpen && (
                <OwnerAddDogModal
                    isOpen={isAddDogModalOpen}
                    onClose={() => setIsAddDogModalOpen(false)}
                    onDogAdded={handleDogAdded}
                />
            )}
        </OwnerLayout>
    );
};

export default OwnerMyDog;